'use client';

import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ClipboardIcon, XCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function OnStationConfig({ 
  isOpen, 
  onClose, 
  onSave, 
  onRemove, 
  apiKey, 
  setApiKey, 
  integration,
  isNewIntegration = false // New prop to explicitly indicate if this is a new integration
}) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(isNewIntegration); // Start in edit mode for new integrations

  // Update editing state when isNewIntegration changes
  useEffect(() => {
    setEditing(isNewIntegration);
  }, [isNewIntegration]);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEditToggle = () => {
    setEditing(!editing);
  };

  const handleSaveClick = () => {
    if (!apiKey.trim()) {
      alert(
        integration?.id
          ? 'API key cannot be empty. Click "Remove Integration" if looking to do so.'
          : 'API key cannot be empty. Please enter a valid API key.'
      );
      return;
    }
    
    console.log("Saving integration with ID:", integration?.id);
    onSave(integration?.id); // Pass the integration ID to the onSave function
    
    if (!isNewIntegration) {
      setEditing(false); // Only reset editing state for existing integrations
    }
  };

  const handleCancelClick = () => {
    if (isNewIntegration) {
      // For new integrations, cancel means close the modal
      onClose();
    } else {
      // For existing integrations, reset to view mode
      setEditing(false);
      setApiKey(integration?.api_key || ''); // Revert to original API key
    }
  };

  const handleRemoveClick = () => {
    if (window.confirm('Are you sure you want to remove this integration?')) {
      onRemove(integration?.id);
    }
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm" />

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-6">
            <Dialog.Panel className="w-full max-w-3xl rounded-lg bg-white shadow-xl p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center border-b pb-3">
                <h2 className="text-xl font-semibold text-gray-800">
                  {isNewIntegration ? 'Add OnStation Integration' : 'Configure OnStation Integration'}
                </h2>
                <button
                  className="text-gray-500 hover:text-gray-800"
                  onClick={onClose}
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Instructions Section */}
              <div className="mt-4 bg-gray-100 p-4 rounded-lg text-gray-700">
                <h3 className="text-md font-semibold mb-2">How to Find Your OnStation API Key:</h3>
                <ul className="list-disc list-inside text-sm space-y-2">
                  <li>Log into your <a href="https://portal.onstationapp.com/" target="_blank" className="text-blue-600 hover:underline">OnStation Portal</a>.</li>
                  <li>Navigate to <strong>Integrations</strong> ➜ <strong>API Settings</strong>.</li>
                  <li>Click <strong>Generate API Key</strong> if you don't have one.</li>
                  <li>Copy the API Key and paste it below.</li>
                  <li>Ensure that you give the key access to your desired projects.</li>
                </ul>
              </div>

              {/* API Key Input */}
              <div className="mt-6">
                <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700">
                  OnStation API Key
                </label>
                <div className="mt-1 relative">
                  <input
                    type={editing ? "text" : "password"} // Show as text when editing, password when viewing
                    id="apiKey"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    readOnly={!editing}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 ${
                      !editing ? 'bg-gray-100' : ''
                    }`}
                    placeholder={editing ? "Enter your OnStation API Key" : ""}
                  />
                  {!editing && apiKey && (
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                      title="Copy to clipboard"
                    >
                      <ClipboardIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {copied && <p className="text-green-600 text-xs mt-1">Copied to clipboard!</p>}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end space-x-3">
                {!editing ? (
                  /* View Mode Buttons */
                  <>
                    <button
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                      onClick={onClose}
                    >
                      Close
                    </button>
                    <button
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-500"
                      onClick={handleEditToggle}
                    >
                      Edit
                    </button>
                  </>
                ) : (
                  /* Edit Mode Buttons */
                  <>
                    {/* Only show Remove button for existing integrations */}
                    {integration?.id && (
                      <button
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-500"
                        onClick={handleRemoveClick}
                        title="Remove Integration"
                      >
                        <TrashIcon className="w-5 h-5 inline mr-1" />
                        Remove Integration
                      </button>
                    )}
                    <button
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-500"
                      onClick={handleSaveClick}
                    >
                      {isNewIntegration ? 'Add Integration' : 'Save Changes'}
                    </button>
                  </>
                )}
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}